/**
 * ASSISTED HELP. UF-07 section 4.7, FR-U-DSP-006. Contained by ADR 0007.
 *
 * The support panel has always listed "any available assisted channel" as an element it did not have.
 * This is it, and the design problem is not the chat bubble. It is honesty.
 *
 * ## Four things this UI must never do
 *
 * **Never present an answer as the platform speaking.** A participant must always be able to tell
 * whether "your daily limit is KES 500,000" came from the cap table or from a language model. So an
 * assistant turn is visually distinct, is labelled, and carries the source it was drawn from.
 *
 * **Never paraphrase the source.** The quote block renders the published passage VERBATIM, which is
 * the tokenization certificate's rule inherited: a paraphrase is where the meaning would quietly go.
 * The model's sentence sits above it, the real words sit below it, and the participant can compare.
 *
 * **Never style a handoff as a failure.** When our articles do not cover something, handing over to a
 * person is the CORRECT outcome, and dressing it in red would teach people that asking for help is an
 * error state. It gets a calm card and a button that goes somewhere useful.
 *
 * **Never let it look like it can act.** It cannot move money, see a balance, or change anything, and
 * the empty state says so in the first screen a participant reads. An assistant that appears able to
 * fix a payment, and then cannot, is worse than no assistant.
 *
 * ## The opt-in is a gate, not a banner
 *
 * The provider is hosted, so the words a participant types leave Kenya. Until they have accepted that
 * notice there is no input box at all: consent is the thing between them and the feature, rather than
 * a line of small print under it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, LipaApiError, type AssistantAvailability, type AssistantCitation } from '@/lib/api';
import { Skeleton } from '@/components/states';

interface Turn {
  role: 'you' | 'assistant';
  body: string;
  citations?: AssistantCitation[];
  handedOff?: boolean;
}

/**
 * A few starting points, so the first screen is not an empty box.
 *
 * Phrased as a person would type them, for the same reason the dispute categories are: somebody who
 * needs help is not composing a search query, they are describing what went wrong.
 */
const SUGGESTIONS = [
  'Why did my payment not go through?',
  'How do I report a problem?',
  'Why is my money still held?',
  'What do I actually own when I hold a token?',
];

export function AssistantDrawer({
  open,
  onClose,
  participantId,
}: {
  open: boolean;
  onClose: () => void;
  participantId: string | null;
}) {
  const [availability, setAvailability] = useState<AssistantAvailability | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [turns, setTurns] = useState<Turn[]>([]);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>();

  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setAvailability(await api.assistantAvailability());
    } catch (e) {
      // The assistant has NO callers and nothing depends on it, so its outage is contained to this
      // panel by design (ADR 0007 rule 5). Say so plainly and point at the channel that does work.
      setError(
        e instanceof LipaApiError ? e.detail.message : 'Assisted help is unavailable right now.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  // Escape closes. A drawer that traps somebody who is already frustrated is a small cruelty.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns, asking]);

  async function accept() {
    if (!participantId || !availability) return;
    setError(null);
    try {
      await api.acceptAssistedHelp(participantId, availability.disclosure.version);
      await load();
      inputRef.current?.focus();
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not record that.');
    }
  }

  async function ask(text: string) {
    const trimmed = text.trim();
    if (!trimmed || asking) return;

    setTurns((t) => [...t, { role: 'you', body: trimmed }]);
    setQuestion('');
    setAsking(true);
    setError(null);

    try {
      const answer = await api.askAssistant(trimmed, conversationId);
      setConversationId(answer.conversation_id);
      setTurns((t) => [
        ...t,
        {
          role: 'assistant',
          body: answer.body,
          citations: answer.citations,
          handedOff: answer.handed_off,
        },
      ]);
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not reach assisted help.');
    } finally {
      setAsking(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label="Assisted help"
    >
      <button
        className="absolute inset-0 bg-ink/30 backdrop-blur-[1px]"
        onClick={onClose}
        aria-label="Close assisted help"
        tabIndex={-1}
      />

      <div className="relative flex h-full w-full max-w-md flex-col border-l border-border bg-canvas shadow-soft">
        <header className="flex items-start justify-between gap-3 border-b border-border bg-surface px-5 py-4">
          <div>
            <h2 className="text-sm font-semibold text-ink">Assisted help</h2>
            {/* Said once, at the top, permanently. Not a tooltip somebody has to go looking for. */}
            <p className="mt-0.5 text-xs text-ink-muted">
              Answers from our help articles. It cannot see your account or move money.
            </p>
          </div>
          <button
            onClick={onClose}
            className="-mr-1 rounded-field px-2 py-1 text-sm text-ink-muted transition hover:bg-surface-sunken hover:text-ink"
          >
            Close
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {loading ? (
            <>
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-20 w-full" />
            </>
          ) : null}

          {error ? (
            <div className="rounded-card border border-border bg-surface p-4">
              <p className="text-sm text-ink">{error}</p>
              <Link
                to="/support"
                onClick={onClose}
                className="mt-3 inline-block rounded-field bg-primary px-3.5 py-2 text-sm font-medium text-white transition hover:bg-primary-light"
              >
                Go to support
              </Link>
            </div>
          ) : null}

          {/* The provider is not switched on. Say that, rather than answering everything with a
              handoff and letting somebody conclude the feature is broken. */}
          {availability && !availability.enabled ? (
            <div className="rounded-card border border-border bg-surface p-4">
              <p className="text-sm font-medium text-ink">Assisted help is not switched on yet</p>
              <p className="mt-1 text-sm text-ink-muted">
                Our support team is available now and can help with anything on your account.
              </p>
              <Link
                to="/support"
                onClick={onClose}
                className="mt-3 inline-block rounded-field bg-primary px-3.5 py-2 text-sm font-medium text-white transition hover:bg-primary-light"
              >
                Contact support
              </Link>
            </div>
          ) : null}

          {/* THE GATE. No input box until this is accepted. */}
          {availability?.enabled && !availability.consented ? (
            <div className="rounded-card border border-border bg-surface p-4">
              <p className="text-sm font-medium text-ink">{availability.disclosure.title}</p>
              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-ink-muted">
                {availability.disclosure.body}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={() => void accept()}
                  disabled={!participantId}
                  className="rounded-field bg-primary px-3.5 py-2 text-sm font-medium text-white transition hover:bg-primary-light disabled:opacity-50"
                >
                  I understand, turn it on
                </button>
                <Link
                  to="/support"
                  onClick={onClose}
                  className="rounded-field border border-border px-3.5 py-2 text-sm font-medium text-ink transition hover:bg-surface-sunken"
                >
                  Talk to a person instead
                </Link>
              </div>
              <p className="mt-3 text-xs text-ink-subtle">
                Version {availability.disclosure.version}. You can use the app fully without this.
              </p>
            </div>
          ) : null}

          {availability?.enabled && availability.consented ? (
            <>
              {turns.length === 0 ? (
                <div>
                  <p className="text-sm text-ink-muted">
                    Ask about how something works. For anything about your own account, money, or a
                    case, our support team is the right place.
                  </p>
                  <ul className="mt-3 space-y-2">
                    {SUGGESTIONS.map((s) => (
                      <li key={s}>
                        <button
                          onClick={() => void ask(s)}
                          className="w-full rounded-card border border-border bg-surface px-3.5 py-2.5 text-left text-sm text-ink transition hover:border-primary/40"
                        >
                          {s}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {turns.map((t, i) => (
                <TurnBubble key={i} turn={t} onNavigate={onClose} />
              ))}

              {asking ? (
                <div
                  className="rounded-card border border-border bg-surface p-4"
                  aria-live="polite"
                >
                  <span className="sr-only">Looking for an answer</span>
                  <Skeleton className="h-3 w-40" />
                  <Skeleton className="mt-2 h-3 w-full" />
                </div>
              ) : null}
            </>
          ) : null}

          <div ref={endRef} />
        </div>

        {availability?.enabled && availability.consented ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void ask(question);
            }}
            className="border-t border-border bg-surface px-5 py-3"
          >
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                maxLength={2000}
                placeholder="Ask about how something works"
                aria-label="Ask assisted help a question"
                className="min-w-0 flex-1 rounded-field border border-border bg-canvas px-3 py-2 text-sm text-ink outline-none transition focus:border-primary"
              />
              <button
                type="submit"
                disabled={!question.trim() || asking}
                className="rounded-field bg-primary px-3.5 py-2 text-sm font-medium text-white transition hover:bg-primary-light disabled:opacity-40"
              >
                Ask
              </button>
            </div>
            {/* The single most important line in the drawer, and it stays on screen while typing
                rather than appearing once at the start and scrolling away. */}
            <p className="mt-2 text-xs text-ink-subtle">
              Do not type your PIN or a password. Answers can be wrong.
            </p>
          </form>
        ) : null}
      </div>
    </div>
  );
}

function TurnBubble({ turn, onNavigate }: { turn: Turn; onNavigate: () => void }) {
  if (turn.role === 'you') {
    return (
      <div className="flex justify-end">
        <p className="max-w-[85%] rounded-card bg-primary px-3.5 py-2.5 text-sm text-white">
          {turn.body}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-card border border-border bg-surface p-4">
      {/* LABELLED, always. A participant must never be unable to tell what is speaking. */}
      <p className="text-xs font-medium uppercase tracking-wide text-ink-subtle">Assisted help</p>
      <p className="mt-1.5 text-sm leading-relaxed text-ink">{turn.body}</p>

      {turn.citations?.length ? (
        <div className="mt-3 space-y-2 border-t border-border pt-3">
          <p className="text-xs font-medium text-ink-subtle">From our help articles</p>
          {turn.citations.map((c) => (
            <blockquote
              key={c.source_id}
              className="rounded-field border-l-2 border-primary/40 bg-surface-sunken px-3 py-2"
            >
              <p className="text-xs font-medium text-ink">{c.title}</p>
              {/* VERBATIM. The published words, so they can be compared with the sentence above. */}
              <p className="mt-1 text-xs leading-relaxed text-ink-muted">{c.quote}</p>
            </blockquote>
          ))}
        </div>
      ) : null}

      {turn.handedOff ? (
        <Link
          to="/support"
          onClick={onNavigate}
          className="mt-3 inline-block rounded-field bg-primary px-3.5 py-2 text-sm font-medium text-white transition hover:bg-primary-light"
        >
          Talk to our support team
        </Link>
      ) : null}
    </div>
  );
}

/** The launcher. Lives on the support hub, which UF-07 calls the single home for help. */
export function AssistantLauncher({ participantId }: { participantId: string | null }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="rounded-field border border-border px-3.5 py-2 text-sm font-medium text-ink transition hover:border-primary/40 hover:bg-surface-sunken"
      >
        Ask for help
      </button>
      <AssistantDrawer open={open} onClose={() => setOpen(false)} participantId={participantId} />
    </>
  );
}
